import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Chip,
  Stack
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import budgetService from '../../../../services/budgetService';
import { getFrequencyOptions, getDayOfWeekOptions, generatePreviewDates } from './utils/recurrenceUtils';

const AddBudgetRecurrenceDialog = ({ open, onClose, onRecurrenceAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    templateId: '',
    startDate: new Date(),
    frequency: 'monthly',
    dayOfMonth: 1,
    dayOfWeek: 1, // Monday
    isActive: true
  });

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewDates, setPreviewDates] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  // Fetch available budget templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setTemplateLoading(true);
        const data = await budgetService.getAllTemplates();
        setTemplates(data);
      } catch (err) {
        console.error('Error fetching budget templates:', err);
        setError('Failed to load budget templates. Please try again later.');
      } finally {
        setTemplateLoading(false);
      }
    };

    if (open) {
      fetchTemplates();
    }
  }, [open]);

  // Generate preview dates when recurrence settings change
  useEffect(() => {
    if (formData.startDate) {
      const dates = generatePreviewDates(formData, 4);
      setPreviewDates(dates);
    }
  }, [formData.startDate, formData.frequency, formData.dayOfMonth, formData.dayOfWeek]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null
      });
    }
  };

  const handleDateChange = (newDate) => {
    setFormData({
      ...formData,
      startDate: newDate
    });
  };

  const validate = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.templateId) {
      errors.templateId = 'Template is required';
    }
    
    if (formData.frequency === 'monthly' && 
        (formData.dayOfMonth < 1 || formData.dayOfMonth > 31)) {
      errors.dayOfMonth = 'Day of month must be between 1 and 31';
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validate();
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Prepare data for API
      const recurrenceData = {
        ...formData,
        startDate: formData.startDate.toISOString()
      };
      
      await budgetService.createRecurrence(recurrenceData);
      onRecurrenceAdded();
    } catch (err) {
      console.error('Error creating budget recurrence:', err);
      setError('Failed to create budget recurrence. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      templateId: '',
      startDate: new Date(),
      frequency: 'monthly',
      dayOfMonth: 1,
      dayOfWeek: 1,
      isActive: true
    });
    setValidationErrors({});
    setError('');
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? null : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Add Budget Recurrence</DialogTitle>
      
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              name="name"
              label="Recurrence Name"
              fullWidth
              margin="normal"
              value={formData.name}
              onChange={handleInputChange}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              disabled={loading}
              autoFocus
            />
            
            <FormControl 
              fullWidth 
              margin="normal" 
              error={!!validationErrors.templateId}
              disabled={loading || templateLoading}
            >
              <InputLabel>Template</InputLabel>
              <Select
                name="templateId"
                value={formData.templateId}
                onChange={handleInputChange}
                label="Template"
              >
                {templateLoading ? (
                  <MenuItem disabled value="">
                    <CircularProgress size={20} /> Loading templates...
                  </MenuItem>
                ) : templates.length === 0 ? (
                  <MenuItem disabled value="">
                    No templates available
                  </MenuItem>
                ) : (
                  templates.map((template) => (
                    <MenuItem key={template._id} value={template._id}>
                      {template.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {validationErrors.templateId && (
                <FormHelperText>{validationErrors.templateId}</FormHelperText>
              )}
            </FormControl>
            
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={formData.startDate}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    margin="normal"
                    fullWidth
                    error={!!validationErrors.startDate}
                    helperText={validationErrors.startDate}
                    disabled={loading}
                  />
                )}
                disabled={loading}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal" disabled={loading}>
              <InputLabel>Frequency</InputLabel>
              <Select
                name="frequency"
                value={formData.frequency}
                onChange={handleInputChange}
                label="Frequency"
              >
                {getFrequencyOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {formData.frequency === 'monthly' && (
              <TextField
                name="dayOfMonth"
                label="Day of Month"
                type="number"
                fullWidth
                margin="normal"
                value={formData.dayOfMonth}
                onChange={handleInputChange}
                error={!!validationErrors.dayOfMonth}
                helperText={validationErrors.dayOfMonth || 'Enter a number between 1-31'}
                disabled={loading}
                InputProps={{ inputProps: { min: 1, max: 31 } }}
              />
            )}
            
            {formData.frequency === 'weekly' && (
              <FormControl fullWidth margin="normal" disabled={loading}>
                <InputLabel>Day of Week</InputLabel>
                <Select
                  name="dayOfWeek"
                  value={formData.dayOfWeek}
                  onChange={handleInputChange}
                  label="Day of Week"
                >
                  {getDayOfWeekOptions().map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Preview of upcoming dates:
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {previewDates.length > 0 ? (
              previewDates.map((date, index) => (
                <Chip 
                  key={index} 
                  label={date} 
                  color={index === 0 ? 'primary' : 'default'} 
                  variant={index === 0 ? 'filled' : 'outlined'}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No preview available
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleReset} 
          disabled={loading}
        >
          Reset
        </Button>
        <Button 
          onClick={onClose} 
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained" 
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Recurrence'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBudgetRecurrenceDialog; 